import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload, RotateCw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { OperationResultStack, type OperationResult } from '../components/OperationResultStack';
import { formatDateTime } from '../lib/format';
import { PageHeader } from '@/components/layout/page-header';

const BANKS = ['VCB', 'ACB', 'VPB', 'VTB', 'BIDV', 'TCB', 'MB', 'STB'] as const;

function defaultPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function AdminStatementsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-statements'],
    queryFn: () => api.listStatements(),
  });
  const [file, setFile] = useState<File | null>(null);
  const [bank, setBank] = useState<string>('VCB');
  const [period, setPeriod] = useState<string>(defaultPeriod());
  const [opResults, setOpResults] = useState<OperationResult[]>([]);

  function pushResult(res: OperationResult) {
    setOpResults((p) => [res, ...p].slice(0, 5));
  }

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected');
      return api.uploadStatement({ file, bank_code: bank, period });
    },
    onSuccess: (res) => {
      pushResult({
        key: `upload-${Date.now()}`,
        kind: 'success',
        title: `Đã upload ${bank} ${period}`,
        detail: `Total ${res.rows.total} · matched ${res.rows.matched} · missing ${res.rows.missing_in_email}${
          res.rows.parse_errors > 0 ? ` · parse errors ${res.rows.parse_errors}` : ''
        }`,
      });
      setFile(null);
      qc.invalidateQueries({ queryKey: ['admin-statements'] });
    },
    onError: (e) =>
      pushResult({
        key: `upload-err-${Date.now()}`,
        kind: 'failure',
        title: 'Upload fail',
        detail: String(e).slice(0, 200),
      }),
  });

  const reconcile = useMutation({
    mutationFn: (id: number) => api.reconcileStatement(id),
    onSuccess: (res) => {
      pushResult({
        key: `recon-${Date.now()}`,
        kind: 'success',
        title: 'Reconcile xong',
        detail: `Total ${res.total} · matched ${res.matched} · missing ${res.missing_in_email}`,
      });
      qc.invalidateQueries({ queryKey: ['admin-statements'] });
    },
  });

  const del = useMutation({
    mutationFn: (id: number) => api.deleteStatement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-statements'] }),
  });

  return (
    <div className="space-y-4">
      <PageHeader title="Statement reconciliation" />

      <OperationResultStack
        items={opResults}
        onDismiss={(k) => setOpResults((p) => p.filter((r) => r.key !== k))}
      />

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 text-sm font-medium text-muted-foreground">Upload statement</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_auto_1fr_auto]">
          <select
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {BANKS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-xs file:font-medium file:text-white"
          />
          <Button
            variant="default"
            size="sm"
            onClick={() => upload.mutate()}
            disabled={!file || upload.isPending}
          >
            <Upload size={14} strokeWidth={1.5} />
            {upload.isPending ? 'Đang upload…' : 'Upload'}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground/70">
          CSV header: <code>occurred_at,amount,merchant,card_last4</code>. Max 1 MB.
        </p>
      </div>

      {isLoading && <Skeleton className="h-32 w-full" />}

      {data && data.rows.length === 0 && (
        <EmptyState
          icon={FileText}
          title="Chưa có statement nào"
          description="Upload CSV statement đầu tiên ở phía trên."
        />
      )}

      {data && data.rows.length > 0 && (
        <div className="space-y-3">
          {data.rows.map((s) => {
            const pct = s.total > 0 ? (s.matched / s.total) * 100 : 0;
            return (
              <div key={s.id} className="rounded-lg border border-border bg-card p-4 text-sm">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-mono text-foreground">{s.bank_code}</span>
                    <span className="text-muted-foreground"> · {s.period_year_month}</span>
                  </div>
                  <span className="text-xs text-muted-foreground/70">
                    {formatDateTime(s.uploaded_at)}
                    {s.uploaded_by_email && ` · ${s.uploaded_by_email}`}
                  </span>
                </div>
                <div className="mb-2 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground">Total</div>
                    <div className="tabular-nums text-foreground">{s.total}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Matched</div>
                    <div className="tabular-nums text-success">{s.matched}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Missing in email</div>
                    <div className="tabular-nums text-warning">{s.missing_in_email}</div>
                  </div>
                </div>
                <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-background">
                  <div className="h-full bg-success transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => reconcile.mutate(s.id)}
                    disabled={reconcile.isPending}
                  >
                    <RotateCw size={12} strokeWidth={1.5} />
                    Re-reconcile
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Soft-delete ${s.bank_code} ${s.period_year_month}?`)) {
                        del.mutate(s.id);
                      }
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
